import React, { useState, useEffect, useCallback } from 'react';
import { saasData, supabase } from '../../lib/supabase';
import './ClassInfoPage.css';

const ClassInfoPage = ({ onBack, onTabChange }) => {
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch class data from Supabase
  const fetchClassData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Starting to fetch class data...');
      
      // Fetch class data from Supabase
      const { data, error: fetchError } = await saasData.getClassData('tn_01');
      
      console.log('Supabase response:', { data, error: fetchError });
      
      if (fetchError) {
        console.error('Error fetching class data:', fetchError);
        setError(fetchError);
        return;
      }
      
      if (!data) {
        console.error('No data returned from Supabase');
        setError('No data returned from database');
        return;
      }
      
      console.log('Raw class data received:', data);
      console.log('Total classes:', data.totalClasses);
      console.log('Total students:', data.totalStudents);
      console.log('Total class masters:', data.totalClassMasters);
      console.log('Data type:', typeof data);
      console.log('Data keys:', Object.keys(data || {}));
      
      // Use fallback data if no classes found
      const processedData = data.totalClasses > 0 ? data : getFallbackData();
      console.log('Processed class data:', processedData);
      
      // Validate the data structure
      const validatedData = validateClassData(processedData);
      console.log('Validated class data:', validatedData);
      
      setClassData(validatedData);
      console.log('Class data loaded successfully:', validatedData);
      
    } catch (err) {
      console.error('Unexpected error in fetchClassData:', err);
      setError(err.message || 'Failed to fetch class data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper functions for class data
  const getClassMasterAvatar = (classMaster) => {
    const name = classMaster.name || 'Unknown'
    return name.charAt(0)
  }

  const getClassMasterDepartment = (classMaster) => {
    return classMaster.department || '未知部門'
  }

  const getClassMasterPosition = (classMaster) => {
    return classMaster.position || '未知職位'
  }

  // Add fallback data if no classes found
  const getFallbackData = () => {
    return {
      totalClasses: 0,
      totalStudents: 0,
      totalClassMasters: 0,
      gradeLevels: {
        '中一級': { name: '中一級', shortName: 'J1', classes: [], studentCount: 0, classMasters: [] },
        '中二級': { name: '中二級', shortName: 'J2', classes: [], studentCount: 0, classMasters: [] },
        '中三級': { name: '中三級', shortName: 'J3', classes: [], studentCount: 0, classMasters: [] },
        '中四級': { name: '中四級', shortName: 'S4', classes: [], studentCount: 0, classMasters: [] },
        '中五級': { name: '中五級', shortName: 'S5', classes: [], studentCount: 0, classMasters: [] },
        '中六級': { name: '中六級', shortName: 'S6', classes: [], studentCount: 0, classMasters: [] }
      },
      genderDistribution: { male: 0, female: 0 },
      ecaParticipation: { participating: 0, notParticipating: 0 },
      academicPerformance: { excellent: 0, needsImprovement: 0 },
      recentClasses: [],
      uniqueClassMasters: []
    };
  };

  // Validate and process class data
  const validateClassData = (data) => {
    if (!data || typeof data !== 'object') {
      console.warn('Invalid class data structure:', data);
      return getFallbackData();
    }

    // Ensure required fields exist
    const validatedData = {
      totalClasses: data.totalClasses || 0,
      totalStudents: data.totalStudents || 0,
      totalClassMasters: data.totalClassMasters || 0,
      gradeLevels: data.gradeLevels || getFallbackData().gradeLevels,
      genderDistribution: data.genderDistribution || { male: 0, female: 0 },
      ecaParticipation: data.ecaParticipation || { participating: 0, notParticipating: 0 },
      academicPerformance: data.academicPerformance || { excellent: 0, needsImprovement: 0 },
      recentClasses: Array.isArray(data.recentClasses) ? data.recentClasses : [],
      uniqueClassMasters: Array.isArray(data.uniqueClassMasters) ? data.uniqueClassMasters : []
    };

    console.log('Validated class data:', validatedData);
    return validatedData;
  };

  useEffect(() => {
    // Test Supabase connection first
    const testConnection = async () => {
      try {
        console.log('Testing Supabase connection for class data...');
        const { data, error } = await supabase
          .from('classroom_management_view')
          .select('count')
          .limit(1);
        
        if (error) {
          console.error('Supabase connection test failed:', error);
        } else {
          console.log('Supabase connection test successful for class data');
        }
      } catch (err) {
        console.error('Supabase connection test error:', err);
      }
    };
    
    testConnection();
    fetchClassData();
  }, [fetchClassData]);

  if (loading) {
    return (
      <div className="class-info-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>班級資訊</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>正在載入班級資料...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="class-info-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>班級資訊</h1>
        </div>
        <div className="error-container">
          <p>載入失敗: {error}</p>
          <button onClick={fetchClassData} className="retry-button">
            重試
          </button>
        </div>
      </div>
    );
  }

  if (!classData) {
    return (
      <div className="class-info-page">
        <div className="page-header">
          <button className="back-button" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>班級資訊</h1>
        </div>
        <div className="no-data-container">
          <p>暫無班級資料</p>
        </div>
      </div>
    );
  }

  return (
    <div className="class-info-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-button" onClick={onBack}>
          ← 返回主頁
        </button>
        <h1>班級資訊</h1>
        <button className="refresh-button" onClick={fetchClassData} title="重新載入">
          🔄
        </button>
      </div>
      
      {/* Debug info */}
      <div style={{ background: '#f0f0f0', padding: '10px', margin: '10px 0', borderRadius: '5px', fontSize: '12px' }}>
        <strong>Debug Info:</strong> Loading: {loading ? 'Yes' : 'No'}, Error: {error || 'None'}, Data: {classData ? 'Loaded' : 'None'}
        <br />
        <button onClick={() => {
          console.log('Manual test button clicked');
          fetchClassData();
        }} style={{ marginTop: '5px', padding: '5px 10px' }}>
          Test Data Fetch
        </button>
      </div>

      {/* Content */}
      <div className="page-content">
        {/* Summary Cards */}
        <div className="summary-section">
          <div className="summary-card">
            <div className="card-icon">🏫</div>
            <div className="card-content">
              <h3>總班級數</h3>
              <div className="card-number">{classData.totalClasses}</div>
              <p>完整配置</p>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="card-icon">👨‍🎓</div>
            <div className="card-content">
              <h3>總學生數</h3>
              <div className="card-number">{classData.totalStudents}</div>
              <p>全校學生</p>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="card-icon">👨‍🏫</div>
            <div className="card-content">
              <h3>班主任數</h3>
              <div className="card-number">{classData.totalClassMasters}</div>
              <p>班級導師</p>
            </div>
          </div>
        </div>

        {/* Grade Distribution */}
        <div className="grade-section">
          <h2>年級班級配置</h2>
          <div className="grade-grid">
            {Object.values(classData.gradeLevels).map((grade, index) => (
              <div key={index} className="grade-card">
                <h3>{grade.name}</h3>
                <div className="grade-number">{grade.classes.length}</div>
                <p>{grade.classes.length}個班別</p>
                <div className="grade-details">
                  {grade.classes && grade.classes.length > 0 ? (
                    <div>
                      <p><strong>學生數:</strong> {grade.studentCount}人</p>
                      <p><strong>班主任:</strong> {grade.classMasters.length}人</p>
                      <div className="class-list">
                        {grade.classes.map((cls, classIndex) => (
                          <div key={classIndex} className="class-item">
                            <span className="class-name">{cls.class_name}</span>
                            {cls.class_master_name && (
                              <span className="class-master">導師: {cls.class_master_name}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <span>暫無班別資料</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Student Statistics */}
        <div className="stats-section">
          <h2>學生統計</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <h4>性別分佈</h4>
              <div className="stat-content">
                <div className="stat-bar">
                  <div 
                    className="stat-fill male" 
                    style={{
                      width: `${classData.genderDistribution.male > 0 ? 
                        (classData.genderDistribution.male / (classData.genderDistribution.male + classData.genderDistribution.female)) * 100 : 0}%`
                    }}
                  ></div>
                </div>
                <div className="stat-labels">
                  <span>男生: {classData.genderDistribution.male > 0 ? 
                    Math.round((classData.genderDistribution.male / (classData.genderDistribution.male + classData.genderDistribution.female)) * 100) : 0}%</span>
                  <span>女生: {classData.genderDistribution.female > 0 ? 
                    Math.round((classData.genderDistribution.female / (classData.genderDistribution.male + classData.genderDistribution.female)) * 100) : 0}%</span>
                </div>
              </div>
            </div>
            
            <div className="stat-item">
              <h4>課外活動參與</h4>
              <div className="stat-content">
                <div className="stat-bar">
                  <div 
                    className="stat-fill eca" 
                    style={{
                      width: `${classData.ecaParticipation.participating > 0 ? 
                        (classData.ecaParticipation.participating / (classData.ecaParticipation.participating + classData.ecaParticipation.notParticipating)) * 100 : 0}%`
                    }}
                  ></div>
                </div>
                <div className="stat-labels">
                  <span>參與: {classData.ecaParticipation.participating > 0 ? 
                    Math.round((classData.ecaParticipation.participating / (classData.ecaParticipation.participating + classData.ecaParticipation.notParticipating)) * 100) : 0}%</span>
                  <span>未參與: {classData.ecaParticipation.notParticipating > 0 ? 
                    Math.round((classData.ecaParticipation.notParticipating / (classData.ecaParticipation.participating + classData.ecaParticipation.notParticipating)) * 100) : 0}%</span>
                </div>
              </div>
            </div>
            
            <div className="stat-item">
              <h4>學業表現</h4>
              <div className="stat-content">
                <div className="stat-bar">
                  <div 
                    className="stat-fill academic" 
                    style={{
                      width: `${classData.academicPerformance.excellent > 0 ? 
                        (classData.academicPerformance.excellent / (classData.academicPerformance.excellent + classData.academicPerformance.needsImprovement)) * 100 : 0}%`
                    }}
                  ></div>
                </div>
                <div className="stat-labels">
                  <span>優秀: {classData.academicPerformance.excellent > 0 ? 
                    Math.round((classData.academicPerformance.excellent / (classData.academicPerformance.excellent + classData.academicPerformance.needsImprovement)) * 100) : 0}%</span>
                  <span>待改進: {classData.academicPerformance.needsImprovement > 0 ? 
                    Math.round((classData.academicPerformance.needsImprovement / (classData.academicPerformance.excellent + classData.academicPerformance.needsImprovement)) * 100) : 0}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Classes */}
        <div className="recent-classes-section">
          <h2>最近註冊班級</h2>
          <div className="classes-list">
            {classData.recentClasses && classData.recentClasses.length > 0 ? (
              classData.recentClasses.map((cls, index) => (
                <div key={cls.class_uuid || index} className="class-item">
                  <div className="class-avatar">
                    {cls.class_name?.charAt(0) || 'C'}
                  </div>
                  <div className="class-info">
                    <h4>{cls.class_name || `班級${index + 1}`}</h4>
                    <p>班級ID: {cls.class_id}</p>
                    {cls.class_master_name && (
                      <span className="class-master">班主任: {cls.class_master_name}</span>
                    )}
                  </div>
                  <div className="class-status">新註冊</div>
                </div>
              ))
            ) : (
              <div className="no-classes">
                <p>暫無最近註冊班級資料</p>
              </div>
            )}
          </div>
        </div>

        {/* Class Masters */}
        <div className="class-masters-section">
          <h2>班主任名單</h2>
          <div className="masters-grid">
            {classData.uniqueClassMasters && classData.uniqueClassMasters.length > 0 ? (
              classData.uniqueClassMasters.map((master, index) => (
                <div key={master.id || index} className="master-card">
                  <div className="master-avatar">
                    {getClassMasterAvatar(master)}
                  </div>
                  <div className="master-info">
                    <h4>{master.name || `老師${index + 1}`}</h4>
                    <p className="master-department">{getClassMasterDepartment(master)}</p>
                    <p className="master-position">{getClassMasterPosition(master)}</p>
                    {master.english_name && (
                      <p className="master-english">{master.english_name}</p>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-masters">
                <p>暫無班主任資料</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassInfoPage;
